import { AcademicCalendar, Holiday, Term } from '../../types';

// Mock data for Academic Calendar page
// UK academic year with terms, holidays, INSET days

export const mockTerms: Term[] = [
  {
    id: 'term-autumn-1',
    name: 'Autumn Term 1',
    startDate: '2024-09-02',
    endDate: '2024-10-25',
    weekCount: 8,
  },
  {
    id: 'term-autumn-2',
    name: 'Autumn Term 2',
    startDate: '2024-11-04',
    endDate: '2024-12-20',
    weekCount: 7,
  },
  {
    id: 'term-spring-1',
    name: 'Spring Term 1',
    startDate: '2025-01-06',
    endDate: '2025-02-14',
    weekCount: 6,
  },
  {
    id: 'term-spring-2',
    name: 'Spring Term 2',
    startDate: '2025-02-24',
    endDate: '2025-04-04',
    weekCount: 6,
  },
  {
    id: 'term-summer-1',
    name: 'Summer Term 1',
    startDate: '2025-04-22',
    endDate: '2025-05-23',
    weekCount: 5,
  },
  {
    id: 'term-summer-2',
    name: 'Summer Term 2',
    startDate: '2025-06-02',
    endDate: '2025-07-18',
    weekCount: 7,
  },
];

export const mockHolidays: Holiday[] = [
  {
    id: 'holiday-autumn-half',
    name: 'Autumn Half Term',
    startDate: '2024-10-28',
    endDate: '2024-11-01',
    type: 'half-term',
  },
  {
    id: 'holiday-christmas',
    name: 'Christmas Holiday',
    startDate: '2024-12-23',
    endDate: '2025-01-03',
    type: 'term-break',
  },
  {
    id: 'holiday-spring-half',
    name: 'Spring Half Term',
    startDate: '2025-02-17',
    endDate: '2025-02-21',
    type: 'half-term',
  },
  {
    id: 'holiday-easter',
    name: 'Easter Holiday',
    startDate: '2025-04-07',
    endDate: '2025-04-18',
    type: 'term-break',
  },
  {
    id: 'holiday-may-bank',
    name: 'May Bank Holiday',
    startDate: '2025-05-05',
    endDate: '2025-05-05',
    type: 'bank-holiday',
  },
  {
    id: 'holiday-may-half',
    name: 'May Half Term',
    startDate: '2025-05-26',
    endDate: '2025-05-30',
    type: 'half-term',
  },
  {
    id: 'holiday-summer',
    name: 'Summer Holiday',
    startDate: '2025-07-21',
    endDate: '2025-08-29',
    type: 'term-break',
  },
];

export const mockInsetDays: Holiday[] = [
  {
    id: 'inset-sept-1',
    name: 'INSET Day - Start of Autumn Term',
    startDate: '2024-09-02',
    endDate: '2024-09-02',
    type: 'inset',
  },
  {
    id: 'inset-oct',
    name: 'INSET Day - Staff Training',
    startDate: '2024-10-11',
    endDate: '2024-10-11',
    type: 'inset',
  },
  {
    id: 'inset-jan',
    name: 'INSET Day - Start of Spring Term',
    startDate: '2025-01-06',
    endDate: '2025-01-06',
    type: 'inset',
  },
  {
    id: 'inset-feb',
    name: 'INSET Day - Staff Development',
    startDate: '2025-02-24',
    endDate: '2025-02-24',
    type: 'inset',
  },
  {
    id: 'inset-july',
    name: 'INSET Day - End of Year',
    startDate: '2025-07-18',
    endDate: '2025-07-18',
    type: 'inset',
  },
];

export const mockAcademicCalendar: AcademicCalendar = {
  id: 'calendar-2024-25',
  academicYear: '2024-2025',
  startDate: '2024-09-02',
  endDate: '2025-07-18',
  terms: mockTerms,
  holidays: [...mockHolidays, ...mockInsetDays],
  totalWeeks: 39,
  totalTeachingDays: 190,
};

// Helper functions

export const getTermByDate = (date: string): Term | undefined => {
  return mockTerms.find((term) => date >= term.startDate && date <= term.endDate);
};

export const getHolidaysByType = (type: Holiday['type']): Holiday[] => {
  return [...mockHolidays, ...mockInsetDays].filter((h) => h.type === type);
};

export const isHoliday = (date: string): boolean => {
  return [...mockHolidays, ...mockInsetDays].some((holiday) => date >= holiday.startDate && date <= holiday.endDate);
};

export const isInsetDay = (date: string): boolean => {
  return mockInsetDays.some((inset) => date >= inset.startDate && date <= inset.endDate);
};

export const isTermTime = (date: string): boolean => {
  return mockTerms.some((term) => date >= term.startDate && date <= term.endDate);
};

// Calendar view helpers

export interface CalendarDay {
  date: string;
  dayOfWeek: number; // 0 = Sunday, 6 = Saturday
  isTermTime: boolean;
  isHoliday: boolean;
  isInset: boolean;
  isWeekend: boolean;
  term?: Term;
  holiday?: Holiday;
}

export const generateCalendarMonth = (year: number, month: number): CalendarDay[] => {
  const days: CalendarDay[] = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dateStr = date.toISOString().split('T')[0];
    const dayOfWeek = date.getDay();

    const term = getTermByDate(dateStr);
    const holiday = [...mockHolidays, ...mockInsetDays].find((h) => dateStr >= h.startDate && dateStr <= h.endDate);

    days.push({
      date: dateStr,
      dayOfWeek,
      isTermTime: !!term,
      isHoliday: !!holiday && holiday.type !== 'inset',
      isInset: !!holiday && holiday.type === 'inset',
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
      term,
      holiday,
    });
  }

  return days;
};

// Summary statistics
export const getAcademicYearStats = () => {
  const totalHolidayDays = mockHolidays.reduce((total, holiday) => {
    const start = new Date(holiday.startDate);
    const end = new Date(holiday.endDate);
    const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return total + days;
  }, 0);

  return {
    totalTerms: mockTerms.length,
    totalWeeks: mockTerms.reduce((total, term) => total + term.weekCount, 0),
    totalHolidays: mockHolidays.length,
    totalInsetDays: mockInsetDays.length,
    totalHolidayDays,
    academicYear: mockAcademicCalendar.academicYear,
  };
};
